/**
 * BearingLinesLayer (issue #171): three.js wrapper for the dashed, pulsing radar
 * and aviation bearing lines built by facilities.ts. Fixed-altitude sky furniture
 * — no terrain sampling, no LOS meaning (hard constraint #1).
 *
 * Rendered as fat lines (Line2 / LineMaterial from three's addons) so the beams
 * have real, screen-constant thickness — a plain THREE.Line ignores linewidth on
 * every WebGL platform and draws a 1 px hairline. LineMaterial also exposes a
 * native `dashOffset` uniform, so the outward dash-march no longer needs the
 * shader-injection shim that LineDashedMaterial required (the old #173 approach).
 * Pixel-space width (worldUnits:false) keeps the lines readable from across the
 * valley regardless of distance, which is the whole point of the layer.
 */
import {
  CanvasTexture,
  Group,
  LinearFilter,
  Sprite,
  SpriteMaterial,
  Vector2,
} from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import type { Facility } from '../core/manifest';
import {
  BEARING_LINES_ALTITUDE_M,
  buildBearingLine,
  type BuiltBearingLine,
} from './facilities';

// Bold marching-dash sizing (issue #171 follow-up): scaled ~6× from the original
// 80 m / 55 m so the bearing lines read as prominent chunks rather than fine
// ticks. Over a ~5–7 km centroid-to-AOI-boundary segment this shows ~6–8 dashes.
const DASH_SIZE_M = 500;
const GAP_SIZE_M = 340;
const TOTAL_SIZE_M = DASH_SIZE_M + GAP_SIZE_M;
const PULSE_HZ = 1.4;
/** Outward dash-march speed (metres/second). Scaled with the dash size so the
 * cadence stays ~one dash period every ~2.8 s (840 m period ÷ 2.8 s ≈ 300). */
const DASH_SCROLL_SPEED_M_PER_S = 300;
/** Screen-space line thickness, CSS pixels (worldUnits:false). ~7× the old 1 px
 * hairline so the beams read as bold ribbons at any distance. */
const LINE_WIDTH_PX = 7;
const LABEL_ALONG_LINE = 0.82;

function makeLabelSprite(text: string, accent: number): Sprite | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const fontPx = 44;
  const pad = 20;
  ctx.font = `600 ${fontPx}px system-ui, sans-serif`;
  const measured = ctx.measureText(text).width;
  const textW = measured > 0 ? measured : text.length * fontPx * 0.55;
  canvas.width = Math.ceil(textW + pad * 2);
  canvas.height = fontPx + pad * 2;

  ctx.font = `600 ${fontPx}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(12, 18, 24, 0.68)';
  roundRect(ctx, 0, 0, canvas.width, canvas.height, 14);
  ctx.fill();
  const r = (accent >> 16) & 0xff;
  const g = (accent >> 8) & 0xff;
  const b = accent & 0xff;
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.85)`;
  ctx.lineWidth = 3;
  roundRect(ctx, 1.5, 1.5, canvas.width - 3, canvas.height - 3, 12);
  ctx.stroke();
  ctx.fillStyle = '#f4f7fa';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const tex = new CanvasTexture(canvas);
  tex.minFilter = LinearFilter;
  const material = new SpriteMaterial({ map: tex, transparent: true, depthWrite: false, fog: false });
  material.toneMapped = false;
  const sprite = new Sprite(material);
  const worldH = 16;
  const aspect = canvas.width / canvas.height;
  sprite.scale.set(worldH * aspect, worldH, 1);
  return sprite;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
}

function labelPosition(line: BuiltBearingLine): { x: number; y: number; z: number } {
  const t = LABEL_ALONG_LINE;
  return {
    x: line.start.x + (line.end.x - line.start.x) * t,
    y: BEARING_LINES_ALTITUDE_M + 12,
    z: line.start.z + (line.end.z - line.start.z) * t,
  };
}

interface BearingLineObject {
  material: LineMaterial;
}

export class BearingLinesLayer {
  readonly group = new Group();
  private readonly lines: BearingLineObject[] = [];
  private elapsedS = 0;
  private dashOffsetM = 0;
  // Fat lines need the viewport size to convert their pixel width into clip
  // space. Seed it from the window so a first frame before setResolution() is
  // still sized sanely; main.ts keeps it in sync on every resize.
  private readonly resolutionPx = new Vector2(
    typeof window === 'undefined' ? 1 : window.innerWidth,
    typeof window === 'undefined' ? 1 : window.innerHeight,
  );

  constructor(facilities: Facility[]) {
    this.group.name = 'bearings';
    for (const facility of facilities) {
      const line = buildBearingLine(facility);
      if (!line) {
        console.warn('bearing line skipped', { id: facility.id, reason: 'invalid coordinates or no AOI exit' });
        continue;
      }

      const y = BEARING_LINES_ALTITUDE_M;
      const geometry = new LineGeometry();
      geometry.setPositions([line.start.x, y, line.start.z, line.end.x, y, line.end.z]);

      const material = new LineMaterial({
        color: line.color,
        linewidth: LINE_WIDTH_PX,
        worldUnits: false,
        dashed: true,
        dashSize: DASH_SIZE_M,
        gapSize: GAP_SIZE_M,
        transparent: true,
        opacity: 0.92,
        depthWrite: false,
        fog: false,
      });
      material.toneMapped = false;
      material.resolution.copy(this.resolutionPx);

      const segment = new Line2(geometry, material);
      segment.computeLineDistances();
      this.group.add(segment);
      this.lines.push({ material });

      const label = makeLabelSprite(line.labelText, line.color);
      if (label) {
        const pos = labelPosition(line);
        label.position.set(pos.x, pos.y, pos.z);
        this.group.add(label);
      }
    }
  }

  /**
   * Keeps the fat-line materials' `resolution` in step with the canvas so the
   * pixel width stays correct after a resize / monitor change. Call once after
   * construction and from the viewer's resize handler.
   */
  setResolution(width: number, height: number): void {
    this.resolutionPx.set(width, height);
    for (const { material } of this.lines) material.resolution.set(width, height);
  }

  /**
   * Advances the opacity pulse and marches the dashes outward — no allocation.
   * The dash-scroll rides LineMaterial's native `dashOffset` uniform; the offset
   * is kept wrapped to one dash period to stay precise over long sessions. A
   * negative offset makes the pattern travel toward the facility end (outward
   * from the array centroid).
   */
  tick(dt: number): void {
    this.elapsedS += dt;
    this.dashOffsetM = (this.dashOffsetM + DASH_SCROLL_SPEED_M_PER_S * dt) % TOTAL_SIZE_M;
    const pulse = 0.72 + 0.28 * (0.5 + 0.5 * Math.sin(this.elapsedS * PULSE_HZ * Math.PI * 2));
    for (const { material } of this.lines) {
      material.opacity = pulse;
      material.dashOffset = -this.dashOffsetM;
    }
  }
}
